import React, { useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Users, Heart, ShieldCheck, ArrowRight, Mail, Flame, BarChart3, Layers, Sparkles } from "lucide-react";

/**
 * Investors — investor relations / fundraising contact page.
 *
 * Brutalist editorial layout, vermilion accents. Three sections:
 *   1. Why now / the opportunity
 *   2. Traction (placeholder numbers — replace with real metrics)
 *   3. Contact form (mailto-backed, no backend persistence yet)
 */

const Kicker = ({ num, label }) => (
  <p className="text-[11px] uppercase tracking-[0.28em] font-black mb-3 flex items-center gap-2">
    <span className="text-vermilion">{num}</span>
    <span className="text-white/40">{label}</span>
  </p>
);

const StatTile = ({ value, label, sub }) => (
  <div>
    <p className="font-display text-5xl sm:text-6xl font-black tracking-tighter leading-none text-vermilion">{value}</p>
    <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-black mt-2">{label}</p>
    {sub && <p className="text-xs text-white/60 mt-1">{sub}</p>}
  </div>
);

const PillarCard = ({ num, icon: Icon, title, body }) => (
  <div className="relative bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 rounded-sm p-6 hover:border-vermilion/50 transition group">
    <p className="text-[10px] tracking-[0.28em] font-black text-vermilion mb-3">{num}</p>
    <Icon size={22} className="text-white/80 mb-3 group-hover:text-vermilion transition" strokeWidth={2} />
    <h3 className="font-display text-lg font-black uppercase tracking-tight leading-tight mb-2">{title}</h3>
    <p className="text-sm text-white/55 leading-relaxed">{body}</p>
  </div>
);

const Investors = () => {
  const [form, setForm] = useState({
    name: "",
    fund: "",
    email: "",
    ticket: "",
    notes: "",
  });

  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent("Investor inquiry — 1o1sound");
    const body = encodeURIComponent(
      `Name: ${form.name}\nFund / firm: ${form.fund}\nEmail: ${form.email}\nIndicative ticket: ${form.ticket}\n\nNotes:\n${form.notes}`
    );
    window.location.href = `mailto:invest@1o1soundapp.net?subject=${subject}&body=${body}`;
  };

  return (
    <div data-testid="investors-page" className="relative">
      {/* Hero */}
      <section className="relative pt-6 pb-14">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-2 right-0 w-[380px] h-[380px] opacity-20 rotate-12"
          style={{ background: "repeating-linear-gradient(45deg, #E84525 0 6px, transparent 6px 14px)" }}
        />
        <Kicker num="01" label="Investor relations" />
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.92] mb-5 max-w-4xl">
          Invest in <span className="text-vermilion">independence</span>.
        </h1>
        <p className="text-base sm:text-lg text-white/65 leading-relaxed max-w-2xl">
          1o1sound is building the streaming platform the music industry actually
          wants — 90/10 in favour of the artist, no label override clauses, no
          dark patterns. We're raising to deepen the catalog, ship lossless, and
          grow the A&R team that's already discovering hits in the Hotspot feed.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-white/55 border border-white/10 rounded-sm px-3 py-1.5">
            <ShieldCheck size={11} /> SAFE · post-money
          </span>
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-white/55 border border-white/10 rounded-sm px-3 py-1.5">
            <Users size={11} /> Open to angels + funds
          </span>
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-white/55 border border-white/10 rounded-sm px-3 py-1.5">
            <Sparkles size={11} /> Strategic LPs welcome
          </span>
        </div>
      </section>

      {/* Why now */}
      <section className="mb-20">
        <Kicker num="02" label="Why now" />
        <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight leading-tight mb-8 max-w-2xl">
          The thesis, three lines deep.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PillarCard
            num="01"
            icon={Heart}
            title="Artists are over the major-label split"
            body="Streaming payouts have flatlined for 4+ years while listener prices climbed. Independent artists are the fastest-growing tier of releases — and the most underserved by current platforms."
          />
          <PillarCard
            num="02"
            icon={BarChart3}
            title="Distribution is unbundling"
            body="Stripe Connect lets us pay artists directly, in real time, with audit trails. The 'we have to be a label' moat that protected legacy streamers is gone."
          />
          <PillarCard
            num="03"
            icon={Layers}
            title="The product wedge is the artist studio"
            body="Upload, lyrics, promo, payouts, badges — all in one app. Free tier funded by audio ads, premium tier at $10/mo. Two-sided flywheel with 90/10 alignment."
          />
        </div>
      </section>

      {/* Traction (placeholder numbers — wire to real /api/admin/metrics later) */}
      <section className="mb-20 border-y border-white/8 py-10">
        <Kicker num="03" label="Traction" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10">
          <StatTile value="90%" label="Artist share" sub="Industry-leading split" />
          <StatTile value="$0.18" label="Net / stream" sub="Vs. $0.003 at majors" />
          <StatTile value="50M+" label="Tracks reachable" sub="iTunes + Deezer + UGC" />
          <StatTile value="3mo" label="Free artist trial" sub="No card required" />
        </div>
        <p className="mt-6 text-xs text-white/40">
          Detailed metrics (MAU, paid conversion, ARPU, payout volume, CAC/LTV) are shared
          under NDA in our data room. Request access below.
        </p>
      </section>

      {/* Contact form */}
      <section className="mb-20">
        <Kicker num="04" label="Get the deck" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-1">
            <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight leading-tight mb-3">
              Let's talk.
            </h2>
            <p className="text-white/55 text-[15px] leading-relaxed mb-5">
              Drop your details. We'll send back the deck, data-room access and
              a calendar link within 48h.
            </p>
            <a
              href="mailto:invest@1o1soundapp.net"
              data-testid="investors-direct-mail"
              className="inline-flex items-center gap-2 text-vermilion text-[12px] font-black uppercase tracking-[0.18em] hover:gap-3 transition-all"
            >
              <Mail size={14} /> invest@1o1soundapp.net <ArrowRight size={14} />
            </a>
          </div>
          <form
            onSubmit={submit}
            data-testid="investors-form"
            className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-white/[0.02] border border-white/10 rounded-sm p-5"
          >
            <input
              required
              data-testid="investors-input-name"
              type="text"
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-black/40 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-vermilion outline-none"
            />
            <input
              data-testid="investors-input-fund"
              type="text"
              placeholder="Fund / firm (optional)"
              value={form.fund}
              onChange={(e) => setForm({ ...form, fund: e.target.value })}
              className="bg-black/40 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-vermilion outline-none"
            />
            <input
              required
              data-testid="investors-input-email"
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-black/40 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-vermilion outline-none"
            />
            <select
              data-testid="investors-input-ticket"
              value={form.ticket}
              onChange={(e) => setForm({ ...form, ticket: e.target.value })}
              className="bg-black/40 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white focus:border-vermilion outline-none"
            >
              <option value="">Indicative ticket (optional)</option>
              <option>$25k–100k</option>
              <option>$100k–250k</option>
              <option>$250k–1M</option>
              <option>$1M+</option>
              <option>Strategic / not financial</option>
            </select>
            <textarea
              data-testid="investors-input-notes"
              placeholder="Anything we should know? (Thesis fit, intros you can make, etc.)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              className="sm:col-span-2 bg-black/40 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-vermilion outline-none resize-none"
            />
            <button
              type="submit"
              data-testid="investors-submit"
              className="sm:col-span-2 mt-1 inline-flex items-center justify-center gap-2 bg-vermilion hover:bg-vermilion-hover text-white text-[12px] font-black uppercase tracking-[0.12em] px-5 py-3.5 rounded-sm transition shadow-[0_12px_36px_-8px_rgba(232,69,37,0.5)]"
            >
              <Flame size={14} /> Request the deck
            </button>
            <p className="sm:col-span-2 text-[10px] text-white/30 mt-1">
              By submitting, you agree we may contact you about this opportunity. No spam — ever.
            </p>
          </form>
        </div>
      </section>

      {/* Back nav */}
      <section className="border-t border-white/10 pt-8 pb-4">
        <Link
          to="/about"
          className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/45 hover:text-white inline-flex items-center gap-2"
        >
          ← Back to about
        </Link>
      </section>
    </div>
  );
};

export default Investors;
